import {Database} from '@tnc/shared';
import isNaN from 'lodash/isNaN';
import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import {useNavigate, useParams} from 'react-router';

import {orderWithDistributorAndRetailerT} from '@/lib/types/order.types';

import {SupabaseInstance} from '@/lib/utils';

import {useItemQuery} from '@/hooks';

import {useAuth} from '@/providers/components/authProvider';
import {useNotifications} from '@/providers/components/notificationsProvider';

/* ******************************************************************
 * OrderContext
 ****************************************************************** */
type OrderContextT = {
  // Properties
  order: orderWithDistributorAndRetailerT | undefined;
  isOrderLoading: boolean;
  orderId: number | undefined;
  transfers: Database['public']['Functions']['get_metrc_transfers_with_package_counts']['Returns'] | [];
  refetchOrder: () => void;
  orderItems: Database['public']['Functions']['get_order_product_summary']['Returns'];
  // Functions
  addMetrcTransferId: (transferId: string) => void;
  handleSubmitItemAssignment: ({
    productId,
    packageId,
    metrcItemId,
    quantity,
  }: {
    productId: number;
    packageId: number;
    metrcItemId: number;
    quantity: number;
  }) => Promise<null | undefined>;
};

const OrderContext = createContext({} as OrderContextT);

/* ******************************************************************
 * OrderProvider
 ****************************************************************** */
export const OrderProvider = ({children}: {children: React.ReactNode}) => {
  const {toast} = useNotifications();
  const navigate = useNavigate();
  const {orderId: orderIdFromParams} = useParams();
  const {userAccounts} = useAuth();
  const isOrderItemSummaryFetchedRef = useRef(false);
  const isTransfersFetchedRef = useRef(false);
  const [orderItems, setOrderItems] = useState<Database['public']['Functions']['get_order_product_summary']['Returns']>(
    []
  );
  const [transfers, setTransfers] = useState<
    Database['public']['Functions']['get_metrc_transfers_with_package_counts']['Returns']
  >([]);

  // Constants
  const orderId = Number(orderIdFromParams);

  // TanStack Query hooks
  const {
    data: fetchedOrder,
    isLoading: isLoadingOrder,
    error: errorOrder,
    refetch: refetchOrder,
  } = useItemQuery({
    table: 'Order',
    id: orderId!,
    columns: ['*', ['Distributor', ['*']], ['Retailer', ['*']], ['Market', ['*']]],
    enabled: !!orderId,
  });

  const fetchTransfersData = useCallback(async () => {
    if (!userAccounts.admin) {
      console.error('User is not logged in');
      return;
    }

    const {data: userData} = await SupabaseInstance.supabase.auth.getUser();

    const {data, error} = await SupabaseInstance.supabase.rpc('get_metrc_transfers_with_package_counts', {
      p_order_id: orderId,
      user_uid: userData?.user?.id ?? '',
    });

    if (error) {
      console.error('Error fetching transfers data:', error);
      toast.error('Error fetching transfers data');
    }

    setTransfers(data || []);
  }, [orderId, toast, userAccounts.admin]);

  /* ******************************************************************
   * Functions
   ******************************************************************* */
  // handle submit item assignment

  /* ******************************************************************
   * Configuration
   ******************************************************************* */
  useEffect(() => {
    // If we have already fetched the order's details or do not yet have a
    // order id from the param hook, return
    if (!orderIdFromParams) return;

    // Verify we have a valid order id
    if (isNaN(orderId) || errorOrder) {
      if (errorOrder) console.error('Error getting order:', errorOrder);
      toast.error('Error getting order details');
      navigate('/admin/orders');
    }
  }, [fetchedOrder, navigate, orderIdFromParams, refetchOrder, errorOrder, orderId, toast]);

  useEffect(() => {
    if (orderId && !isTransfersFetchedRef.current) {
      isTransfersFetchedRef.current = true;
      fetchTransfersData();
    }
  }, [orderId, fetchTransfersData]);

  /* ******************************************************************
   * Functions
   ******************************************************************* */
  /**
   * Fetches the order item summary for the order
   * @returns {Promise<void>}
   */
  const getOrderItemSummary = useCallback(async () => {
    if (!userAccounts.admin) {
      console.error('User is not logged in');
      return;
    }

    const {data: userData} = await SupabaseInstance.supabase.auth.getUser();

    const {data, error} = await SupabaseInstance.supabase.rpc('get_order_product_summary', {
      user_uid: userData?.user?.id ?? '',
      p_order_id: orderId,
    });

    if (error) {
      console.error('Error getting order item summary:', error);
      toast.error('Error getting order item summary');
    }

    setOrderItems(data || []);
    return data;
  }, [userAccounts.admin, orderId, toast]);

  /**
   * Handles the submission of item assignments
   * @param {Object} params - The parameters for the assignment
   * @returns {Promise<null | undefined>}
   */
  const handleSubmitItemAssignment = useCallback(
    async ({
      productId,
      packageId,
      metrcItemId,
      quantity,
    }: {
      productId: number;
      packageId: number;
      metrcItemId: number;
      quantity: number;
    }) => {
      const {data, error} = await SupabaseInstance.supabase.rpc('assign_package_items_to_items', {
        _order_id: orderId,
        _metric_package_id: packageId,
        _metric_item_id: metrcItemId,
        _quantity: quantity,
        _product_id: productId,
      });

      if (error) {
        console.error('Error assigning package items to items:', error);
        toast.error('Error assigning package items to items');
      }

      // refetch transfers
      fetchTransfersData();
      getOrderItemSummary();

      return data;
    },
    [orderId, fetchTransfersData, getOrderItemSummary, toast]
  );

  /**
   * Adds a metrc_transfer_id to the order
   * @param {number} transferId - The transfer id to add
   * @returns {Promise<void>}
   */
  const addMetrcTransferId = useCallback(
    async (transferId: string) => {
      if (!userAccounts.admin) {
        console.error('User is not logged in');
        return;
      }

      // check if the transfer id already exists
      if (transfers.some(transfer => transfer.manifest_number === transferId)) {
        toast.error('This transfer ID already exists for this order.');
        return;
      }

      // Check if the transfer id is valid
      const {data: transfer, error: transferError} = await SupabaseInstance.supabase
        .from('Metric_Transfer')
        .select('*')
        .eq('manifest_number', transferId)
        .single();

      if (transferError) {
        console.error('Error getting transfer:', transferError);
        toast.error('Error: No transfer found with that manifest number');
        return;
      }

      if (transfer) {
        // Check if the transfer id already exists in the many to many table
        const {data: existingTransfer} = await SupabaseInstance.supabase
          .from('Order_Metrc_Transfers')
          .select('*')
          .eq('fk_metrc_transfer_id', transfer.metrc_transfer_id)
          .eq('fk_order_id', orderId)
          .single();

        if (existingTransfer) {
          toast.error('This transfer ID already exists for this order.');
          return;
        }

        const {data: userData} = await SupabaseInstance.supabase.auth.getUser();

        // Add the transfer id to the many to many table
        const {error: newTransferError} = await SupabaseInstance.supabase.from('Order_Metrc_Transfers').insert({
          fk_metrc_transfer_id: transfer.metrc_transfer_id,
          fk_order_id: orderId,
          created_by: userData?.user?.id ?? '',
          date_created: new Date().toISOString(),
        });

        if (newTransferError) {
          console.error('Error adding transfer ID to order:', newTransferError);
          toast.error('Error adding transfer ID to order');
        }

        // Update the transfers state
        fetchTransfersData();
      } else {
        toast.error('Invalid transfer ID');
      }
    },
    [userAccounts.admin, transfers, toast, orderId, fetchTransfersData]
  );

  /**
   * Fetches the order item summary for the order
   * @returns {Promise<void>}
   */
  useEffect(() => {
    if (orderId && !isOrderItemSummaryFetchedRef.current) {
      isOrderItemSummaryFetchedRef.current = true;
      getOrderItemSummary();
    }
  }, [orderId, getOrderItemSummary]);

  return (
    <OrderContext.Provider
      value={{
        order: fetchedOrder,
        orderId: fetchedOrder?.order_id,
        isOrderLoading: isLoadingOrder,
        transfers,
        refetchOrder,
        orderItems,

        // Functions
        addMetrcTransferId,
        handleSubmitItemAssignment,
      }}
    >
      {children}
    </OrderContext.Provider>
  );
};

/* ******************************************************************
 * useOrder Hook
 ****************************************************************** */
export const useOrder = () => {
  const context = useContext(OrderContext);
  if (context && Object.keys(context).length === 0 && Object.getPrototypeOf(context) === Object.prototype) {
    throw new Error('userOrder must be used within a OrderProvider');
  }

  return context;
};
